import { useCallback, useEffect, useRef, useState } from 'react';
import { readerApi, reportApi } from '../services/api';
import { pickReader, setLastReaderId } from '../utils/localPreferences';

/**
 * Logging a chapter from inside the reader (WS7).
 *
 * "Reading in the app counts as reading" — both the audio player finishing a
 * chapter and the Mark as read button go through here, so they produce the
 * same report and the same post-log moment.
 *
 * @param {Object} [options]
 * @param {string} [options.userId] scopes the remembered reader
 */
export function useChapterLogger({ userId } = {}) {
  const [readers, setReaders] = useState([]);
  const [reader, setReader] = useState(null);
  const [moment, setMoment] = useState(null);
  const [momentLabel, setMomentLabel] = useState('');
  const [recap, setRecap] = useState(null);
  const [reportIds, setReportIds] = useState([]);
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState(null);

  // Chapters logged this session, so a repeated `ended` event or a double
  // click can't file the same chapter twice.
  const loggedRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    readerApi.getReaders()
      .then(response => {
        if (cancelled) return;
        const list = response.data.readers || [];
        setReaders(list);
        setReader(pickReader(list, userId));
      })
      .catch(err => console.error('Failed to load readers for logging:', err));
    return () => { cancelled = true; };
  }, [userId]);

  const selectReader = useCallback((next) => {
    setReader(next);
    setLastReaderId(userId, next?.id);
  }, [userId]);

  /**
   * Log one chapter for the selected reader.
   * @param {Object} [options]
   * @param {boolean} [options.recap] ask for the before -> after recap. Only
   *   manual logs (Mark as read) do; an audio auto-log shouldn't.
   * @returns {Promise<boolean>} whether a report was created
   */
  const logChapter = useCallback(async (chapter, { recap: wantsRecap = false } = {}) => {
    if (!chapter || !reader) return false;
    if (loggedRef.current.has(chapter.id)) return false;

    try {
      setLogging(true);
      setError(null);
      loggedRef.current.add(chapter.id);

      const response = await reportApi.createReport({
        readerId: reader.id,
        chapterIds: [chapter.id],
        ...(wantsRecap ? { recap: true } : {})
      });

      setMoment(response.data.moment);
      setRecap(response.data.recap || null);
      setMomentLabel(`${chapter.book} ${chapter.chapter}`);
      setReportIds((response.data.reports || []).map(r => r.id));
      return true;
    } catch (err) {
      console.error('Failed to log chapter:', err);
      // Let them try again if it failed
      loggedRef.current.delete(chapter.id);
      setError(err.response?.data?.error || 'Could not log that chapter.');
      return false;
    } finally {
      setLogging(false);
    }
  }, [reader]);

  const wasLogged = useCallback((chapter) => (
    chapter ? loggedRef.current.has(chapter.id) : false
  ), []);

  const clearMoment = useCallback(() => setMoment(null), []);

  const undo = useCallback(async () => {
    try {
      await reportApi.undoLastBatch();
      loggedRef.current.clear();
      setMoment(null);
    } catch (err) {
      console.error('Failed to undo:', err);
      setError('Could not undo that. Try the Reports tab on My Progress.');
    }
  }, []);

  const saveReflection = useCallback(async (text) => {
    await Promise.all(reportIds.map(id => reportApi.updateReport(id, { notes: text })));
  }, [reportIds]);

  return {
    readers,
    reader,
    selectReader,
    logChapter,
    wasLogged,
    logging,
    error,
    moment,
    momentLabel,
    recap,
    clearMoment,
    undo,
    saveReflection,
    hasReports: reportIds.length > 0
  };
}

export default useChapterLogger;
